/**
 * Library Folders API Hooks
 * Klasör listesi, oluşturma, yeniden adlandırma, silme ve toplu ekle/çıkar
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../config/api-client";
import { API_ENDPOINTS } from "../config/api-endpoints";
import {
  BulkFolderLibraryRequest,
  LibraryFolder,
} from "../models/api-types";
import { patchLibraryItemsInCache } from "./library-cache";
import { useToast } from "@/features/toast";
import useAuthStore from "@/store/use-auth-store";

interface ApiEnvelope<T> {
  statusCode: number;
  success: boolean;
  data: T;
}

export const LIBRARY_FOLDERS_QUERY_KEY = ["library-folders"] as const;

export const useLibraryFolders = () => {
  const { isAuthenticated } = useAuthStore();

  return useQuery<LibraryFolder[]>({
    queryKey: LIBRARY_FOLDERS_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<ApiEnvelope<LibraryFolder[]>>(
        API_ENDPOINTS.libraryFolders
      );
      return response.data ?? [];
    },
    enabled: isAuthenticated(),
    staleTime: 60 * 1000,
  });
};

export const useLibraryFolderMutations = () => {
  const queryClient = useQueryClient();
  const { success: successToast, error: errorToast } = useToast();
  const { t } = useTranslation();

  const invalidateFolders = () =>
    queryClient.invalidateQueries({ queryKey: LIBRARY_FOLDERS_QUERY_KEY });

  const createMutation = useMutation<LibraryFolder, Error, string>({
    mutationFn: async (name) => {
      const response = await apiClient.post<ApiEnvelope<LibraryFolder>>(
        API_ENDPOINTS.libraryFolders,
        { name }
      );
      return response.data;
    },
    onSuccess: () => invalidateFolders(),
    onError: () => errorToast(t("library.folders.createError")),
  });

  const renameMutation = useMutation<
    LibraryFolder,
    Error,
    { folderId: string; name: string }
  >({
    mutationFn: async ({ folderId, name }) => {
      const response = await apiClient.patch<ApiEnvelope<LibraryFolder>>(
        API_ENDPOINTS.libraryFolder(folderId),
        { name }
      );
      return response.data;
    },
    onSuccess: () => invalidateFolders(),
    onError: () => errorToast(t("library.folders.updateError")),
  });

  const deleteMutation = useMutation<unknown, Error, string>({
    mutationFn: (folderId) =>
      apiClient.delete(API_ENDPOINTS.libraryFolder(folderId)),
    onSuccess: () => {
      invalidateFolders();
      queryClient.invalidateQueries({ queryKey: ["library"] });
    },
    onError: () => errorToast(t("library.folders.deleteError")),
  });

  const bulkFolderMutation = useMutation<
    unknown,
    Error,
    BulkFolderLibraryRequest
  >({
    mutationFn: (request) =>
      apiClient.post(API_ENDPOINTS.libraryBulkFolder, request),
    onMutate: ({ ids, folderId, action }) => {
      patchLibraryItemsInCache(queryClient, ids, (item) => {
        const current = item.folderIds ?? [];
        const folderIds =
          action === "add"
            ? Array.from(new Set([...current, folderId]))
            : current.filter((id) => id !== folderId);
        return { ...item, folderIds };
      });
    },
    onSuccess: (_data, { action }) => {
      successToast(
        action === "add"
          ? t("library.folders.added")
          : t("library.folders.removed")
      );
    },
    onError: () => errorToast(t("library.folders.updateError")),
    onSettled: () => {
      invalidateFolders();
      queryClient.invalidateQueries({ queryKey: ["library"] });
    },
  });

  return {
    createFolder: createMutation.mutateAsync,
    isCreatingFolder: createMutation.isPending,
    renameFolder: renameMutation.mutate,
    deleteFolder: deleteMutation.mutate,
    updateItemsFolder: bulkFolderMutation.mutate,
    isUpdatingItemsFolder: bulkFolderMutation.isPending,
  };
};
